import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  OnChanges,
} from '@angular/core';

/**
 * DEV / DEBUG global panel: floating, draggable, collapsible window that pretty-prints
 * any state object as JSON. Reusable across the app.
 *
 *   <app-debug-state [state]="anyObject" title="PAGE WIZARD"></app-debug-state>
 *
 * Visibility: shown ONLY when localStorage `show_debug` is '1' or 'true' — nothing
 * else reveals it (not even a dev build). Enable with
 *   localStorage.setItem('show_debug', '1')
 * and disable with
 *   localStorage.removeItem('show_debug')
 *
 * The host element is relocated to <body> so an ancestor's transform/overflow can't clip
 * it (position: fixed then resolves against the viewport). Position + collapsed state are
 * persisted per-instance via [storageKey] so multiple panels remember their own spot.
 */
@Component({
  selector: 'app-debug-state',
  templateUrl: './debug-state.component.html',
  styleUrls: ['./debug-state.component.scss'],
})
export class DebugStateComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  /** Object to display. Serialized safely (cycles handled) each change. */
  @Input() state: any;
  /** Header label. */
  @Input() title = 'DEBUG STATE';
  /** Namespace for persisted position/collapsed so instances don't clash. */
  @Input() storageKey = 'debug-state';

  @ViewChild('panel', { static: false }) panel!: ElementRef<HTMLElement>;

  /** Whether the panel renders at all (dev build or debug flag set). */
  visible = false;
  collapsed = false;
  /** Pretty-printed JSON of `state`. Recomputed on change. */
  rendered = '';

  // Drag state
  private dragging = false;
  private startX = 0;
  private startY = 0;
  private originLeft = 0;
  private originTop = 0;
  private moveHandler = (e: PointerEvent) => this.onPointerMove(e);
  private upHandler = () => this.onPointerUp();

  constructor() {
    this.visible = this.resolveVisibility();
  }

  ngOnChanges(): void {
    this.rendered = this.stringify(this.state);
  }

  ngAfterViewInit(): void {
    if (!this.visible || !this.panel?.nativeElement) return;
    const el = this.panel.nativeElement;

    // Relocate to <body> so ancestor transforms/overflow can't clip a fixed element.
    document.body.appendChild(el);

    // Restore persisted collapsed state + position.
    this.collapsed = localStorage.getItem(this.posKey('collapsed')) === '1';
    const savedPos = this.readPos();
    if (savedPos) {
      el.style.left = savedPos.left + 'px';
      el.style.top = savedPos.top + 'px';
      el.style.right = 'auto';
      el.style.bottom = 'auto';
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('pointermove', this.moveHandler);
    window.removeEventListener('pointerup', this.upHandler);
    const el = this.panel?.nativeElement;
    if (el?.parentNode) el.parentNode.removeChild(el);
  }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem(this.posKey('collapsed'), this.collapsed ? '1' : '0');
  }

  /** Copy the current JSON to the clipboard. */
  copyState(event: Event): void {
    event.stopPropagation();
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(this.rendered).catch(() => {});
    }
  }

  // --- Dragging -----------------------------------------------------------

  onHeaderPointerDown(event: PointerEvent): void {
    // Ignore drags that start on a button (collapse/copy).
    if ((event.target as HTMLElement)?.closest('button')) return;
    const el = this.panel?.nativeElement;
    if (!el) return;

    this.dragging = true;
    const rect = el.getBoundingClientRect();
    this.originLeft = rect.left;
    this.originTop = rect.top;
    this.startX = event.clientX;
    this.startY = event.clientY;

    // Switch to left/top positioning for free movement.
    el.style.left = rect.left + 'px';
    el.style.top = rect.top + 'px';
    el.style.right = 'auto';
    el.style.bottom = 'auto';

    window.addEventListener('pointermove', this.moveHandler);
    window.addEventListener('pointerup', this.upHandler);
    event.preventDefault();
  }

  private onPointerMove(event: PointerEvent): void {
    if (!this.dragging) return;
    const el = this.panel?.nativeElement;
    if (!el) return;

    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    // Clamp within the viewport.
    let left = this.originLeft + dx;
    let top = this.originTop + dy;
    left = Math.max(0, Math.min(left, window.innerWidth - width));
    top = Math.max(0, Math.min(top, window.innerHeight - height));

    el.style.left = left + 'px';
    el.style.top = top + 'px';
  }

  private onPointerUp(): void {
    if (!this.dragging) return;
    this.dragging = false;
    window.removeEventListener('pointermove', this.moveHandler);
    window.removeEventListener('pointerup', this.upHandler);

    const el = this.panel?.nativeElement;
    if (el) {
      this.writePos(el.offsetLeft, el.offsetTop);
    }
  }

  // --- Helpers ------------------------------------------------------------

  private resolveVisibility(): boolean {
    // Shown only when localStorage `show_debug` is '1' or 'true' — nothing else
    // (not even a dev build) reveals it. Toggle with:
    //   localStorage.setItem('show_debug', '1')   / removeItem('show_debug')
    try {
      const flag = (localStorage.getItem('show_debug') ?? '')
        .trim()
        .toLowerCase();
      return flag === '1' || flag === 'true';
    } catch {
      return false;
    }
  }

  /** JSON.stringify that tolerates circular references. */
  private stringify(value: any): string {
    const seen = new WeakSet();
    try {
      return JSON.stringify(
        value,
        (_key, val) => {
          if (typeof val === 'object' && val !== null) {
            if (seen.has(val)) return '[Circular]';
            seen.add(val);
          }
          if (typeof val === 'function') return '[Function]';
          return val;
        },
        2,
      );
    } catch {
      return String(value);
    }
  }

  private posKey(suffix: string): string {
    return `${this.storageKey}:${suffix}`;
  }

  private readPos(): { left: number; top: number } | null {
    try {
      const raw = localStorage.getItem(this.posKey('pos'));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') {
        return parsed;
      }
    } catch {}
    return null;
  }

  private writePos(left: number, top: number): void {
    try {
      localStorage.setItem(this.posKey('pos'), JSON.stringify({ left, top }));
    } catch {}
  }
}
